"use client";

import styles from "./Footer.module.scss";

// Helpers
import Link from "next/link";
import type {FooterLink} from "./interface";

// Icons
import {
    InstagramOutlined,
    TikTokFilled,
    YoutubeFilled,
} from "@/app/[lang]/components/ui/AntdLite";
import {FaFacebook} from "@react-icons/all-files/fa/FaFacebook";
import {FaLinkedin} from "@react-icons/all-files/fa/FaLinkedin";
import PhoneIcon from "../../../../../../public/phone.svg";
import EmailIcon from "../../../../../../public/email.svg";
import anpcSal from "../../../../../../public/anpc-sal.png";
import anpcSol from "../../../../../../public/anpc-sol.png";

// Components
import Logo from "../Logo/Logo";
import {Divider, Flex, Text} from "@/app/[lang]/components/ui/AntdLite";
import Image from "@/app/[lang]/components/shared/SmartImage";
import {useHrefFromPageOrURL} from "@/app/[lang]/utils/useHrefFromPageOrURL";
import {getStrapiMedia} from "@/app/[lang]/utils/api-helpers";

type linkProps = {
    bold: boolean;
    hidden?: boolean;
    id: number;
    newTab: boolean;
    page: any;
    text: string;
    url: string | null;
}

type footerSectionsProps = {
    hidden?: boolean;
    id: number;
    links: linkProps[];
    title?: string;
}

type FooterProps = {
    hidden?: boolean;
    title: string;
    content: string;
    copyrightText: string;
    email: string
    emailDescription?: string | null;
    footerLogo: any;
    footerSections: footerSectionsProps[];
    id: number;
    phoneNumber: string;
    phoneNumberDescription: string;
    socialLinkHeading?: any;
    socialLinks: any[];
};

function Footer({footer}: { footer: FooterProps }) {

    const renderIcon = (social: string) => {
        switch (social) {
            case "FACEBOOK":
                return <FaFacebook/>;
            case "INSTAGRAM":
                return <InstagramOutlined/>;
            case "YOUTUBE":
                return <YoutubeFilled/>;
            case "LINKEDIN":
                return <FaLinkedin/>;
            case "TIKTOK":
                return <TikTokFilled/>;
            default:
                return null;
        }
    };

    return (
        <footer className={styles.wrapper}>
            <div className="container">
                {/*<Divider className={styles.divider}/>*/}

                <div className={styles.footerSections}>
                    <div className={styles.footerColumns}>
                        {footer.footerSections.filter((item) => !item?.hidden).map((section: any) => (
                            <Flex vertical key={section.id} className={styles.menuColumn}>
                                <h4 className={styles.heading}>{section.title}</h4>

                                {section.links.filter((item: linkProps) => !item.hidden).map((link: FooterLink) => {
                                    return (
                                        <FooterLink key={link.id} link={link}/>
                                    )
                                })}
                            </Flex>
                        ))}
                    </div>

                    <Divider className={styles.divider}/>

                    <div className={styles.bottomSection}>
                        <Flex vertical gap={8} className={styles.infoDetails}>
                            <Logo
                                alt={footer?.footerLogo?.logoImg?.data?.attributes?.alternativeText || "PPC Blue Romania"}
                                className={styles.logo}
                                src={getStrapiMedia(footer?.footerLogo?.logoImg?.data?.attributes?.url)}
                            />
                            <Text className={styles.copyrightText}>
                                {footer.copyrightText}
                            </Text>
                        </Flex>

                        <Flex vertical gap={8} className={styles.infoDetails}>
                            <Image src={PhoneIcon}  width={25} loading="lazy" decoding="async"
                                   height={24} className={styles.phoneIcon} alt="PPC Blue Romania Phone"/>

                            <Text className={styles.text}>
                                <a href={`tel:${footer.phoneNumber}`}>{footer.phoneNumber}</a>{" "}
                                <span>{footer.phoneNumberDescription}</span>
                            </Text>
                        </Flex>

                        <Flex vertical gap={8} className={styles.infoDetails}>
                            <Image src={EmailIcon} width={25} loading="lazy" decoding="async"
                                   height={24}  className={styles.phoneIcon} alt="PPC Blue Romania Email"/>

                            <Text className={styles.text}>
                                <a href={`mailto:${footer.email}`}>{footer.email}</a>{" "}
                                {footer.emailDescription}
                            </Text>
                        </Flex>

                        <Flex gap={8} className={styles.infoANPC}>
                            <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="nofollow noopener noreferrer">
                                <Image
                                    loading="lazy"
                                    decoding="async"
                                    width={250}
                                    height={50}
                                    src={anpcSal}
                                    className={styles.imgANPC}
                                    alt="Logo ANPC"
                                />
                            </a>

                            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="nofollow noopener noreferrer">
                                <Image
                                    loading="lazy"
                                    decoding="async"
                                    width={250}
                                    height={50}
                                    src={anpcSol}
                                    className={styles.imgANPC}
                                    alt="Logo ANPC"
                                />
                            </a>
                        </Flex>

                        {!!footer?.socialLinks?.length &&
                            <Flex vertical gap={8} className={styles.infoDetails}>
                                <Text className={styles.text}>{footer.socialLinkHeading}</Text>

                                <Flex gap={24} className={styles.socials}>
                                    {footer?.socialLinks?.map((link: any) => {
                                        return (
                                            <a href={link.url} key={`${link.social ?? "social"}-${link.url ?? ""}`}>
                                                {renderIcon(link.social)}
                                            </a>
                                        );
                                    })}
                                </Flex>
                            </Flex>
                        }

                    </div>
                </div>
            </div>
        </footer>
    );
}

type FooterLinkProps = {
    link: FooterLink
}

const FooterLink: React.FC<FooterLinkProps> = ({link}) => {
    const href = useHrefFromPageOrURL(link);
    const isExternal = /^https?:\/\//.test(href ?? "");

    if (isExternal) {
        return (
            <a
                href={href ?? ""}
                target={link.newTab ? "_blank" : "_self"}
                rel={link.newTab ? "noopener noreferrer" : undefined}
                className={styles.menuItem}
            >
                {link.text}
            </a>
        );
    }

    return (
        <Link
            href={href ?? ""}
            target={link.newTab ? "_blank" : "_self"}
            className={styles.menuItem}
        >
            {link.text}
        </Link>
    )
}

export default Footer;
